import React from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Slider,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import SlideAssetZone from './SlideAssetZone';
import SlideChartEditor from './SlideChartEditor';
import StockSearchPanel from './StockSearchPanel';
import { LAYOUT_LABELS, FONT_OPTIONS, normalizeTipografia, DEFAULT_TIPOGRAFIA } from '../utils/slideSchema';
import { ARCA_TEMPLATES } from '../utils/arcaTemplates';
import { SLIDE_THEMES, HUD } from '../utils/slideThemes';

const tabSx = {
  borderBottom: `1px solid ${HUD.border}`,
  minHeight: 44,
  '& .MuiTab-root': { color: HUD.muted, fontSize: '0.8rem', fontWeight: 600, minHeight: 44 },
  '& .Mui-selected': { color: HUD.accent },
  '& .MuiTabs-indicator': { bgcolor: HUD.accent },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: HUD.text,
    '& fieldset': { borderColor: HUD.border },
    '&:hover fieldset': { borderColor: 'rgba(56,189,248,0.35)' },
    '&.Mui-focused fieldset': { borderColor: HUD.accent },
  },
  '& .MuiInputLabel-root': { color: HUD.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: HUD.accent },
};

export default function ArcaInspectorPanel({
  inspectorTab,
  onTabChange,
  activeSlide,
  onSlideChange,
  apresentacaoNome,
  onApresentacaoNomeChange,
  modo,
  onModoChange,
  tomIa,
  onTomIaChange,
  prompt,
  onPromptChange,
  deckQuantidade,
  onDeckQuantidadeChange,
  onGerarSlide,
  onGerarDeck,
  onMelhorarSlide,
  onImportFiles,
  onGerarImagem,
  onAddTemplate,
  onImportStockAsset,
  usarWeb,
  onUsarWebChange,
  generating,
  generatingDeck,
  improving,
  generatingImage,
  importing,
  exporting,
  showIlustracaoHint,
  onApplyTheme,
}) {
  const themeId = activeSlide?.theme || 'cyber-hud';
  const tipografia = normalizeTipografia(activeSlide?.tipografia);
  const busy = generating || generatingDeck || improving || importing || exporting || generatingImage;

  const patchTipografia = (patch) => {
    if (!activeSlide) return;
    onSlideChange({
      ...activeSlide,
      tipografia: { ...normalizeTipografia(activeSlide.tipografia), ...patch },
    });
  };

  const fontId =
    FONT_OPTIONS.find((f) => f.value === tipografia.fontFamily)?.id || FONT_OPTIONS[0].id;

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: 320 },
        flexShrink: 0,
        bgcolor: HUD.bgPanel,
        borderLeft: { lg: `1px solid ${HUD.border}` },
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Tabs value={inspectorTab} onChange={(_, v) => onTabChange(v)} variant="fullWidth" sx={tabSx}>
        <Tab label="Design" />
        <Tab label="Configurações" />
      </Tabs>

      <Box sx={{ p: 2.5, flex: 1, overflowY: 'auto' }}>
        {inspectorTab === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="overline" sx={{ color: HUD.muted, letterSpacing: 1 }}>
              Templates
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {ARCA_TEMPLATES.map((t) => (
                <Chip
                  key={t.id}
                  label={t.label}
                  size="small"
                  onClick={() => onAddTemplate?.(t.id)}
                  disabled={busy}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: HUD.bgElevated,
                    color: HUD.text,
                    border: `1px solid ${HUD.border}`,
                    '&:hover': { borderColor: HUD.borderAccent, color: HUD.accent },
                  }}
                />
              ))}
            </Box>

            <Divider sx={{ borderColor: HUD.border }} />

            <Typography variant="overline" sx={{ color: HUD.muted, letterSpacing: 1 }}>
              Tema visual
            </Typography>
            {Object.values(SLIDE_THEMES).map((theme) => {
              const selected = themeId === theme.id;
              return (
                <Box
                  key={theme.id}
                  onClick={() => onApplyTheme(theme.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: selected ? 'rgba(56,189,248,0.12)' : HUD.bgElevated,
                    border: `1px solid ${selected ? HUD.borderAccent : HUD.border}`,
                    transition: 'border-color 0.15s, background 0.15s',
                    '&:hover': { borderColor: HUD.borderAccent },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      background: `linear-gradient(135deg, ${theme.bg} 50%, ${theme.accent} 50%)`,
                      border: `1px solid ${theme.border}`,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: HUD.text, fontWeight: selected ? 700 : 500 }}>
                      {theme.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: HUD.subtle }}>
                      {theme.accent}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            <Divider sx={{ borderColor: HUD.border }} />

            <Typography variant="overline" sx={{ color: HUD.muted, letterSpacing: 1 }}>
              Layout do slide
            </Typography>
            <FormControl fullWidth size="small" sx={fieldSx} disabled={!activeSlide}>
              <InputLabel>Layout</InputLabel>
              <Select
                label="Layout"
                value={activeSlide?.layout || 'titulo-bullets'}
                onChange={(e) => activeSlide && onSlideChange({ ...activeSlide, layout: e.target.value })}
              >
                {Object.entries(LAYOUT_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ borderColor: HUD.border }} />

            <Typography variant="overline" sx={{ color: HUD.muted, letterSpacing: 1 }}>
              Tipografia
            </Typography>
            <FormControl fullWidth size="small" sx={fieldSx} disabled={!activeSlide}>
              <InputLabel>Fonte</InputLabel>
              <Select
                label="Fonte"
                value={fontId}
                onChange={(e) => {
                  const opt = FONT_OPTIONS.find((f) => f.id === e.target.value);
                  if (opt) patchTipografia({ fontFamily: opt.value });
                }}
              >
                {FONT_OPTIONS.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="caption" sx={{ color: HUD.muted }}>
                Título ({tipografia.tituloSize}px)
              </Typography>
              <Slider
                size="small"
                min={18}
                max={48}
                value={tipografia.tituloSize || DEFAULT_TIPOGRAFIA.tituloSize}
                disabled={!activeSlide}
                onChange={(_, v) => patchTipografia({ tituloSize: v })}
                sx={{ color: HUD.accent }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: HUD.muted }}>
                Subtítulo ({tipografia.subtituloSize}px)
              </Typography>
              <Slider
                size="small"
                min={12}
                max={28}
                value={tipografia.subtituloSize || DEFAULT_TIPOGRAFIA.subtituloSize}
                disabled={!activeSlide}
                onChange={(_, v) => patchTipografia({ subtituloSize: v })}
                sx={{ color: HUD.accent }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: HUD.muted }}>
                Bullets ({tipografia.bulletSize}px)
              </Typography>
              <Slider
                size="small"
                min={11}
                max={22}
                value={tipografia.bulletSize || DEFAULT_TIPOGRAFIA.bulletSize}
                disabled={!activeSlide}
                onChange={(_, v) => patchTipografia({ bulletSize: v })}
                sx={{ color: HUD.accent }}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!tipografia.boldTitulo}
                  disabled={!activeSlide}
                  onChange={(e) => patchTipografia({ boldTitulo: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: HUD.muted }}>
                  Título em negrito
                </Typography>
              }
              sx={{ ml: 0 }}
            />

            <ToggleButtonGroup
              exclusive
              size="small"
              fullWidth
              value={tipografia.align || 'left'}
              disabled={!activeSlide}
              onChange={(_, v) => v && patchTipografia({ align: v })}
              sx={{
                '& .MuiToggleButton-root': {
                  color: HUD.muted,
                  borderColor: HUD.border,
                  textTransform: 'none',
                  '&.Mui-selected': { bgcolor: 'rgba(56,189,248,0.15)', color: HUD.accent },
                },
              }}
            >
              <ToggleButton value="left">Esq.</ToggleButton>
              <ToggleButton value="center">Centro</ToggleButton>
              <ToggleButton value="right">Dir.</ToggleButton>
            </ToggleButtonGroup>

            <Divider sx={{ borderColor: HUD.border }} />

            {activeSlide && (
              <SlideChartEditor
                graficos={activeSlide.graficos || []}
                onChange={(graficos) => onSlideChange({ ...activeSlide, graficos })}
              />
            )}

            <Divider sx={{ borderColor: HUD.border }} />

            <StockSearchPanel
              disabled={busy || !activeSlide}
              onImportAsset={onImportStockAsset}
            />

            {activeSlide && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  size="small"
                  label={`${activeSlide.bullets?.length || 0} bullets`}
                  sx={{ bgcolor: HUD.bgElevated, color: HUD.muted, border: `1px solid ${HUD.border}` }}
                />
                <Chip
                  size="small"
                  label={`${activeSlide.graficos?.length || 0} gráficos`}
                  sx={{ bgcolor: HUD.bgElevated, color: HUD.muted, border: `1px solid ${HUD.border}` }}
                />
                <Chip
                  size="small"
                  label={`${activeSlide.midias?.length || 0} mídias`}
                  sx={{ bgcolor: HUD.bgElevated, color: HUD.muted, border: `1px solid ${HUD.border}` }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome da apresentação"
              size="small"
              fullWidth
              value={apresentacaoNome}
              onChange={(e) => onApresentacaoNomeChange(e.target.value)}
              sx={fieldSx}
            />

            <Box>
              <Typography variant="caption" sx={{ color: HUD.muted, fontWeight: 600, mb: 1, display: 'block' }}>
                Modo de contexto (IA gratuita — Gemini)
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={modo}
                onChange={(_, v) => v && onModoChange(v)}
                sx={{
                  '& .MuiToggleButton-root': {
                    color: HUD.muted,
                    borderColor: HUD.border,
                    textTransform: 'none',
                    '&.Mui-selected': { bgcolor: 'rgba(56,189,248,0.15)', color: HUD.accent },
                  },
                }}
              >
                <ToggleButton value="conectado">
                  <DatasetLinkedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Conectado
                </ToggleButton>
                <ToggleButton value="livre">
                  <EditNoteIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Livre
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Tom da IA</InputLabel>
              <Select
                label="Tom da IA"
                value={tomIa || 'executivo'}
                onChange={(e) => onTomIaChange?.(e.target.value)}
              >
                <MenuItem value="executivo">Executivo</MenuItem>
                <MenuItem value="tecnico">Técnico</MenuItem>
                <MenuItem value="didatico">Didático</MenuItem>
                <MenuItem value="institucional">Institucional</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!usarWeb}
                  onChange={(e) => onUsarWebChange?.(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: HUD.muted }}>
                  Busca web (grounding) — opcional
                </Typography>
              }
              sx={{ ml: 0 }}
            />
            <Typography variant="caption" sx={{ color: HUD.subtle, mt: -1, lineHeight: 1.4 }}>
              Pode falhar no plano free; nesse caso a IA gera sem internet.
            </Typography>

            <TextField
              label="Prompt / tema"
              multiline
              minRows={4}
              maxRows={8}
              fullWidth
              value={prompt}
              onChange={(e) => onPromptChange(e.target.value)}
              sx={fieldSx}
            />

            <Button
              variant="contained"
              fullWidth
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              disabled={busy}
              onClick={onGerarSlide}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: HUD.accent,
                color: HUD.bgDeep,
                '&:hover': { bgcolor: '#0ea5e9' },
              }}
            >
              {generating ? 'Gerando...' : 'Gerar slide com IA'}
            </Button>

            <Box>
              <Typography variant="caption" sx={{ color: HUD.muted }}>
                Slides do deck: {deckQuantidade || 5}
              </Typography>
              <Slider
                size="small"
                min={3}
                max={8}
                step={1}
                marks
                value={deckQuantidade || 5}
                onChange={(_, v) => onDeckQuantidadeChange?.(v)}
                sx={{ color: HUD.accent }}
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              startIcon={
                generatingDeck ? <CircularProgress size={16} color="inherit" /> : <ViewCarouselIcon />
              }
              disabled={busy}
              onClick={onGerarDeck}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: 'rgba(56,189,248,0.2)',
                color: HUD.accent,
                border: `1px solid ${HUD.borderAccent}`,
                '&:hover': { bgcolor: 'rgba(56,189,248,0.3)' },
              }}
            >
              {generatingDeck ? 'Gerando deck...' : 'Gerar apresentação (N slides)'}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={improving ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              disabled={busy || !activeSlide}
              onClick={onMelhorarSlide}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderColor: HUD.borderAccent,
                color: HUD.accent,
              }}
            >
              {improving ? 'Melhorando...' : 'Melhorar este slide (IA)'}
            </Button>

            <Typography variant="caption" sx={{ color: HUD.subtle, lineHeight: 1.45 }}>
              Texto e decks usam Gemini free. Ilustrações por IA podem exigir plano pago — use upload PNG/JPG.
            </Typography>

            <SlideAssetZone
              onFiles={onImportFiles}
              onGerarImagem={onGerarImagem}
              disabled={busy}
              importing={importing}
              generatingImage={generatingImage}
              showIlustracaoHint={showIlustracaoHint}
              variant="hud"
            />

            <Divider sx={{ borderColor: HUD.border }} />

            <Typography variant="overline" sx={{ color: HUD.muted, letterSpacing: 1 }}>
              Comportamento
            </Typography>
            <Typography variant="caption" sx={{ color: HUD.subtle, lineHeight: 1.5 }}>
              Proporção fixa 16:9 no canvas. Exportações PPTX/PDF respeitam o conteúdo editável do slide ativo.
            </Typography>
            <FormControlLabel
              control={<Switch defaultChecked size="small" color="primary" />}
              label={
                <Typography variant="body2" sx={{ color: HUD.muted }}>
                  Centralizar slide no canvas
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
